/**
 * ErrorLogger - Centralized error logging and monitoring utility for Supabase operations
 * Tracks migration failures, query errors, and RLS policy violations
 */

const ERROR_TYPES = {
  MIGRATION: 'migration_error',
  QUERY: 'query_error',
  POLICY_VIOLATION: 'policy_violation',
  AUTH: 'auth_error',
  NETWORK: 'network_error',
  UNKNOWN: 'unknown_error',
};

const SEVERITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

const MAX_STORED_ERRORS = 100; // Maximum errors to store in localStorage

class ErrorLogger {
  constructor() {
    this.storageKey = 'quilline_error_logs';
    this.listeners = [];
  }

  /**
   * Log a migration error
   * @param {Error} error - The error object
   * @param {string} migrationFile - Migration file name
   * @param {object} additionalContext - Additional context
   */
  logMigrationError(error, migrationFile, additionalContext = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.MIGRATION,
      severity: SEVERITY_LEVELS?.CRITICAL,
      message: error?.message || 'Migration failed',
      error,
      context: {
        migrationFile,
        stack: error?.stack,
        ...additionalContext,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Log a query error
   * @param {Error} error - The error object
   * @param {object} queryDetails - Query details (table, operation, etc.)
   * @param {object} userContext - User context
   */
  logQueryError(error, queryDetails = {}, userContext = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.QUERY,
      severity: this._determineQuerySeverity(error),
      message: error?.message || 'Query failed',
      error,
      context: {
        table: queryDetails?.table,
        operation: queryDetails?.operation, // SELECT, INSERT, UPDATE, DELETE
        query: queryDetails?.query,
        errorCode: error?.code,
        errorDetails: error?.details,
        hint: error?.hint,
        userId: userContext?.userId,
        userRole: userContext?.userRole,
        stack: error?.stack,
        ...queryDetails?.additionalContext,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Log an RLS policy violation
   * @param {Error} error - The error object
   * @param {object} policyDetails - Policy violation details
   * @param {object} userContext - User context
   */
  logPolicyViolation(error, policyDetails = {}, userContext = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.POLICY_VIOLATION,
      severity: SEVERITY_LEVELS?.HIGH,
      message: error?.message || 'RLS policy violation',
      error,
      context: {
        policyName: policyDetails?.policyName,
        table: policyDetails?.table,
        operation: policyDetails?.operation,
        userId: userContext?.userId,
        userRole: userContext?.userRole,
        attemptedAction: policyDetails?.attemptedAction,
        errorCode: error?.code,
        errorDetails: error?.details,
        stack: error?.stack,
        ...policyDetails?.additionalContext,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Log an authentication error
   * @param {Error} error - The error object
   * @param {object} authContext - Authentication context
   */
  logAuthError(error, authContext = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.AUTH,
      severity: SEVERITY_LEVELS?.MEDIUM,
      message: error?.message || 'Authentication failed',
      error,
      context: {
        action: authContext?.action, // signIn, signUp, signOut, resetPassword
        email: authContext?.email,
        errorCode: error?.code,
        stack: error?.stack,
        ...authContext?.additionalContext,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Log a network error
   * @param {Error} error - The error object
   * @param {object} context - Additional context
   */
  logNetworkError(error, context = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.NETWORK,
      severity: SEVERITY_LEVELS?.MEDIUM,
      message: error?.message || 'Network error',
      error,
      context: {
        url: context?.url,
        method: context?.method,
        stack: error?.stack,
        ...context,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Log a general error
   * @param {Error} error - The error object
   * @param {object} context - Additional context
   */
  logError(error, context = {}) {
    const errorLog = this._createErrorLog({
      type: ERROR_TYPES?.UNKNOWN,
      severity: SEVERITY_LEVELS?.MEDIUM,
      message: error?.message || 'An error occurred',
      error,
      context: {
        stack: error?.stack,
        ...context,
      },
    });

    this._persistError(errorLog);
    this._logToConsole(errorLog);
    this._notifyListeners(errorLog);

    return errorLog;
  }

  /**
   * Get all stored error logs
   * @param {object} filters - Optional filters (type, severity, startDate, endDate)
   * @returns {Array} Array of error logs
   */
  getErrorLogs(filters = {}) {
    try {
      const logs = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      
      if (!filters || Object.keys(filters)?.length === 0) {
        return logs;
      }

      return logs?.filter(log => {
        if (filters?.type && log?.type !== filters?.type) return false;
        if (filters?.severity && log?.severity !== filters?.severity) return false;
        if (filters?.startDate && new Date(log.timestamp) < new Date(filters.startDate)) return false;
        if (filters?.endDate && new Date(log.timestamp) > new Date(filters.endDate)) return false;
        return true;
      });
    } catch (error) {
      console.error('Failed to retrieve error logs:', error);
      return [];
    }
  }

  /**
   * Clear all error logs
   */
  clearErrorLogs() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('Error logs cleared');
    } catch (error) {
      console.error('Failed to clear error logs:', error);
    }
  }

  /**
   * Clear old error logs (older than specified days)
   * @param {number} days - Number of days to keep
   */
  clearOldLogs(days = 7) {
    try {
      const logs = this.getErrorLogs();
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - days);

      const filteredLogs = logs?.filter(log => new Date(log.timestamp) > cutoffDate);
      localStorage.setItem(this.storageKey, JSON.stringify(filteredLogs));
      console.log(`Cleared logs older than ${days} days`);
    } catch (error) {
      console.error('Failed to clear old logs:', error);
    }
  }

  /**
   * Subscribe to error events
   * @param {Function} callback - Callback function to receive error logs
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(listener => listener !== callback);
    };
  }

  /**
   * Export error logs as JSON
   * @returns {string} JSON string of error logs
   */
  exportLogs() {
    const logs = this.getErrorLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Get error statistics
   * @returns {object} Error statistics
   */
  getStatistics() {
    const logs = this.getErrorLogs();
    
    const stats = {
      total: logs?.length,
      byType: {},
      bySeverity: {},
      last24Hours: 0,
      last7Days: 0,
    };

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    logs?.forEach(log => {
      // Count by type
      stats.byType[log.type] = (stats?.byType?.[log?.type] || 0) + 1;
      
      // Count by severity
      stats.bySeverity[log.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
      
      // Count recent errors
      const logDate = new Date(log.timestamp);
      if (logDate > oneDayAgo) stats.last24Hours++;
      if (logDate > sevenDaysAgo) stats.last7Days++;
    });

    return stats;
  }

  // Private methods

  _createErrorLog({ type, severity, message, error, context }) {
    return {
      id: this._generateId(),
      type,
      severity,
      message,
      timestamp: new Date()?.toISOString(),
      errorName: error?.name,
      errorMessage: error?.message,
      context,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location?.href : 'unknown',
    };
  }

  _persistError(errorLog) {
    try {
      const logs = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      logs?.unshift(errorLog); // Add to beginning
      
      // Keep only the most recent errors
      const trimmedLogs = logs?.slice(0, MAX_STORED_ERRORS);
      
      localStorage.setItem(this.storageKey, JSON.stringify(trimmedLogs));
    } catch (error) {
      console.error('Failed to persist error log:', error);
    }
  }

  _logToConsole(errorLog) {
    const consoleStyle = this._getConsoleStyle(errorLog?.severity);
    
    console.group(`%c[${errorLog?.type?.toUpperCase()}] ${errorLog?.severity?.toUpperCase()}`, consoleStyle);
    console.log('Message:', errorLog?.message);
    console.log('Timestamp:', errorLog?.timestamp);
    console.log('Context:', errorLog?.context);
    if (errorLog?.context?.stack) {
      console.log('Stack Trace:', errorLog?.context?.stack);
    }
    console.groupEnd();
  }

  _getConsoleStyle(severity) {
    const styles = {
      [SEVERITY_LEVELS?.LOW]: 'color: #3b82f6; font-weight: bold;',
      [SEVERITY_LEVELS?.MEDIUM]: 'color: #f59e0b; font-weight: bold;',
      [SEVERITY_LEVELS?.HIGH]: 'color: #ef4444; font-weight: bold;',
      [SEVERITY_LEVELS?.CRITICAL]: 'color: #dc2626; font-weight: bold; font-size: 14px;',
    };
    return styles?.[severity] || styles?.[SEVERITY_LEVELS?.MEDIUM];
  }

  _determineQuerySeverity(error) {
    // PGRST116 = No rows found (not critical)
    if (error?.code === 'PGRST116') return SEVERITY_LEVELS?.LOW;
    
    // Network errors
    if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      return SEVERITY_LEVELS?.MEDIUM;
    }
    
    // Permission/policy errors
    if (error?.code === '42501' || error?.message?.includes('permission')) {
      return SEVERITY_LEVELS?.HIGH;
    }
    
    // Default to medium
    return SEVERITY_LEVELS?.MEDIUM;
  }

  _notifyListeners(errorLog) {
    this.listeners?.forEach(listener => {
      try {
        listener(errorLog);
      } catch (error) {
        console.error('Error in error listener:', error);
      }
    });
  }

  _generateId() {
    return `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
  }

  /**
   * Format error for Sentry or other monitoring services
   * @param {object} errorLog - Error log object
   * @returns {object} Sentry-compatible error object
   */
  toSentryFormat(errorLog) {
    return {
      message: errorLog?.message,
      level: this._mapSeverityToSentryLevel(errorLog?.severity),
      timestamp: new Date(errorLog.timestamp)?.getTime() / 1000,
      tags: {
        error_type: errorLog?.type,
        severity: errorLog?.severity,
      },
      extra: errorLog?.context,
      user: errorLog?.context?.userId ? {
        id: errorLog?.context?.userId,
        role: errorLog?.context?.userRole,
      } : undefined,
    };
  }

  _mapSeverityToSentryLevel(severity) {
    const mapping = {
      [SEVERITY_LEVELS?.LOW]: 'info',
      [SEVERITY_LEVELS?.MEDIUM]: 'warning',
      [SEVERITY_LEVELS?.HIGH]: 'error',
      [SEVERITY_LEVELS?.CRITICAL]: 'fatal',
    };
    return mapping?.[severity] || 'error';
  }
}

// Export singleton instance
export const errorLogger = new ErrorLogger();

// Export constants for external use
export { ERROR_TYPES, SEVERITY_LEVELS };

export default errorLogger;
